import React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { EmptyState, Spinner } from '@aibuild/ui';
import { usePageTitle } from '@contexts/usePageTitle';
import { FeatureGate, useFeatureGate } from '@featureFlags/index';
import AppCard from './AppCard';
import { appPath } from './appRunPath';
import AppGallerySections from './builder/AppGallerySections';
import { useAppCatalog } from './useApps';
import { CardGrid, PageBody, PageRoot, StateWrap } from './AppsGalleryPage.styled';

/**
 * The list of apps. Picking one opens its composer and nothing more — the run
 * and its conversation are created when the user presses Run, so browsing the
 * gallery never litters the task list with runs that never happened.
 */
const AppsGalleryPage: React.FC = () => {
  const intl = useIntl();
  usePageTitle(
    intl.formatMessage({ id: 'apps.gallery.title', defaultMessage: 'Apps' }),
    intl.formatMessage({
      id: 'apps.gallery.help',
      defaultMessage:
        'Single-purpose jobs built on the agent. Give one the files it asks for and it does the rest.',
    }),
  );
  const navigate = useNavigate();
  const catalog = useAppCatalog();
  const builderEnabled = useFeatureGate(FeatureGate.OS_APP_BUILDER);
  const openApp = (appKey: string) => navigate(appPath(appKey));

  let body: React.ReactNode;
  if (catalog.isLoading) {
    body = (
      <StateWrap>
        <Spinner size="lg" />
      </StateWrap>
    );
  } else if (catalog.isError) {
    body = (
      <StateWrap>
        <EmptyState
          title={intl.formatMessage({
            id: 'apps.gallery.error.title',
            defaultMessage: 'Apps could not be loaded',
          })}
          description={intl.formatMessage({
            id: 'apps.gallery.error.body',
            defaultMessage: 'Check your connection and reload the page.',
          })}
        />
      </StateWrap>
    );
  } else if (builderEnabled) {
    body = <AppGallerySections catalog={catalog.data ?? []} onOpenApp={openApp} />;
  } else if (!catalog.data?.length) {
    body = (
      <StateWrap>
        <EmptyState
          title={intl.formatMessage({
            id: 'apps.gallery.empty.title',
            defaultMessage: 'No apps yet',
          })}
          description={intl.formatMessage({
            id: 'apps.gallery.empty.body',
            defaultMessage: 'Apps will appear here as they are released.',
          })}
        />
      </StateWrap>
    );
  } else {
    body = (
      <CardGrid data-testid="apps-gallery__grid">
        {catalog.data.map((entry) => (
          <AppCard
            key={entry.appKey}
            appKey={entry.appKey}
            manifest={entry.manifest}
            onOpen={() => openApp(entry.appKey)}
          />
        ))}
      </CardGrid>
    );
  }

  return (
    <PageRoot data-testid="apps-gallery">
      <PageBody>{body}</PageBody>
    </PageRoot>
  );
};

export default AppsGalleryPage;
